import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem } from '@yola/ws-ui';
import getFontPreviewVariant from '../helpers/get-font-preview-variant';
import { HEADING_PREVIEW_TEXT, PARAGRAPH_PREVIEW_TEXT } from '../constants/text-samples';

const { PresetContainer } = designSystem;

function FontPairPreset({
  id,
  title,
  previewUrl = null,
  isSelected = false,
  observer,
  onClick,
  onEditClick = null,
  onDeleteClick = null,
  editLabel = '',
  deleteLabel = '',
  isCustom = false,
  variables = {},
}) {
  const presetRef = useRef(null);
  const isMobile = bowser.mobile;
  const { primaryFontFamily, secondaryFontFamily } = variables;
  let primaryFontWeight;
  let secondaryFontWeight;

  if (isCustom) {
    primaryFontWeight = getFontPreviewVariant(primaryFontFamily);
    secondaryFontWeight = getFontPreviewVariant(secondaryFontFamily);
  }

  useEffect(() => {
    observer.observe(presetRef.current);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const handleClick = () => onClick(id);
  const handleEditClick = () => onEditClick(id);
  const handleDeleteClick = () => onDeleteClick(id);

  return (
    <div id={id} className="ws-font-pair-preset" ref={presetRef}>
      <PresetContainer
        title={title}
        isSelected={isSelected}
        {...(!isMobile && { withTitleTooltip: true })}
        onClick={handleClick}
        {...(onEditClick && {
          editLabel,
          onEditClick: handleEditClick,
        })}
        {...(isCustom &&
          onDeleteClick && {
            deleteLabel,
            onDeleteClick: handleDeleteClick,
          })}
      >
        {isCustom ? (
          <div className="ws-font-pair-preset__custom">
            <h1
              className="ws-font-pair-preset__custom--heading"
              style={{ fontFamily: primaryFontFamily, fontWeight: primaryFontWeight }}
            >
              {HEADING_PREVIEW_TEXT}
            </h1>
            <p
              className="ws-font-pair-preset__custom--paragraph"
              style={{ fontFamily: secondaryFontFamily, fontWeight: secondaryFontWeight }}
            >
              {PARAGRAPH_PREVIEW_TEXT}
            </p>
          </div>
        ) : (
          <img className="ws-font-pair-preset__image" src={previewUrl} alt={title} />
        )}
      </PresetContainer>
    </div>
  );
}

FontPairPreset.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  previewUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  isSelected: PropTypes.bool,
  observer: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onDeleteClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  editLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
  isCustom: PropTypes.bool,
  variables: PropTypes.shape({
    primaryFontFamily: PropTypes.string,
    secondaryFontFamily: PropTypes.string,
  }),
};

export default FontPairPreset;
